// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface CopyableKeyProps {
  value: string;
  className?: string;
}

/**
 * A muted, monospace key shown as a secondary line under an entity's name — click anywhere on it to
 * copy, with a copy icon on the right. Used across the list pages (chains, stores, settings,
 * vendors, transformations) as the name sub-line when the dedicated Key column is hidden.
 */
export function CopyableKey({ value, className }: CopyableKeyProps) {
  const { t } = useTranslation("back-office");
  const [copied, setCopied] = useState(false);

  function handleCopy(e: React.MouseEvent) {
    // Don't trigger row navigation/selection when copying from within a table row
    e.stopPropagation();
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast(
      <span>
        {t("common.copiedToClipboard")} <b className="break-all">{value}</b>
      </span>,
    );
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        "inline-flex w-fit items-center gap-1 font-mono text-xs font-normal text-muted-foreground transition-colors hover:text-foreground",
        className,
      )}
    >
      <span>{value}</span>
      {copied ? (
        <Check className="size-3 shrink-0 text-emerald-600" />
      ) : (
        <Copy className="size-3 shrink-0 opacity-50" />
      )}
    </button>
  );
}
